import React, { useContext, useState, useEffect, useMemo, useRef } from "react";
import {
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  makeStyles,
} from "@material-ui/core";
import { gql } from "apollo-boost";
import { useMutation, useQuery } from "@apollo/react-hooks";
import moment from "moment";
import Countdown from "react-countdown";
import { MobileContext } from "components/common";
// eslint-disable-next-line no-unused-vars
import * as Types from "../CreditMonitoring.types";
import { CreditMonitoringErrorBox } from "../ErrorBox";
import { VerificationContinueButton } from "./ContinueButton";

const VERIFY_AUTH_QUESTION_MUTATION = gql`
  mutation verifyTransUnionAuthentication(
    $verifyAnswerInput: VerifyAnswerInput!
  ) {
    verifyTransUnionAuthentication(verifyAnswerInput: $verifyAnswerInput) {
      status
      multiChoiceQuestions {
        questionPosition
        questionText
        questionId
        key
        answerChoices {
          answerPosition
          answerText
          answerId
          key
        }
      }
    }
  }
`;

const GET_AUTH_QUESTION_QUERY = gql`
  query ($blackbox: String!) {
    info: TransUnionAuthentication(blackbox: $blackbox) {
      fullfillmentKey
      multiChoiceQuestions {
        questionPosition
        questionText
        questionId
        key
        answerChoices {
          answerPosition
          answerText
          answerId
        }
      }
    }
  }
`;

const useStyles = makeStyles({
  radio: {
    "&.Mui-checked": {
      color: "#00A588",
    },
  },
});

/**
 * @param {Object} props
 * @param {Types.Question} props.question
 * @param {Function} props.onChoiceSelect
 */
const SingleQuestion = (props) => {
  const { question, onChoiceSelect } = props;
  const classes = useStyles();
  const {
    questionId,
    questionText,
    questionPosition,
    key,
    answerChoices: choices,
  } = question;
  return (
    <div className="mt-6">
      <p className="font-bold mb-2">
        {questionPosition}
        {". "}
        {questionText}
      </p>
      <RadioGroup onChange={(e) => onChoiceSelect(questionId, e.target.value, key)}>
        {choices?.map((choice) => (
          <div key={choice.answerText}>
            <FormControlLabel
              value={choice.answerId}
              control={<Radio className={classes.radio} />}
              label={choice.answerText}
              className="font-normal"
            />
          </div>
        ))}
      </RadioGroup>
    </div>
  );
};

const DEFAULT_ERROR_MESSAGE = "Could not initiate your verification. Please try again later. If the problem persists, please contact support.";
const DEFAULT_TIMER = Date.now() + 15 * 60 * 1000;

/**
 * @param {Object} props
 * @param {Function} props.onContinue
 * @param {Function} props.onLockedOut
 * @param {Types.Question[]} props.questions
 * @param {string} props.fullfillmentKey
 * @param {Function} props.updateQuestions
 */
export const VerificationQuestionnaire = ({ onContinue, onLockedOut }) => {
  const blackbox = useMemo(
    () => (window.IGLOO?.getBlackbox() || {}).blackbox,
    [],
  );
  const { isMobile } = useContext(MobileContext);
  const [selectedAnswers, setSelectedAnswers] = useState(new Map());
  const { data: questionsData, loading: questionsLoading, error: getQuestionsError } = useQuery(
    GET_AUTH_QUESTION_QUERY,
    { variables: { blackbox } },
  );

  const [
    verifyAuthQuestionsMutation,
    { loading, error: verifyAuthQuestionsError },
  ] = useMutation(VERIFY_AUTH_QUESTION_MUTATION);

  // we have a separate state for questions because we need to update the questions when we request a new otp
  const [questions, setQuestions] = useState([]);
  const [fullfillmentKey, setFullfillmentKey] = useState("");
  const isFullfillmentKeySet = useRef(false);

  // handle the case where the questions are not valid
  // e.g server or network error
  const questionsAreValid = Array.isArray(questions) && questions.length > 0;

  const [otp, setOtp] = useState("");
  const [errorTitle, setErrorTitle] = useState("");
  const [errorMessage, setErrorMessage] = useState(
    questionsAreValid
      ? ""
      : "",
  );
  const [formError, setFormError] = useState("");
  const [isLocked, setIsLocked] = useState(false);
  const [otpExireTime, setOtpExireTime] = useState(DEFAULT_TIMER);

  useEffect(() => {
    if (
      questionsData &&
      questionsData.info
    ) {
      if (!(Array.isArray(questionsData.info.multiChoiceQuestions) && questionsData.info.multiChoiceQuestions.length > 0)) {
        setErrorMessage(DEFAULT_ERROR_MESSAGE);
      }
      setQuestions(questionsData.info?.multiChoiceQuestions || []);
      if (!isFullfillmentKeySet.current) {
        setFullfillmentKey(questionsData.info.fullfillmentKey);
        isFullfillmentKeySet.current = true;
      }
    }
  }, [questionsData]);

  const lockUser = () => {
    setIsLocked(true);
    onLockedOut();
  };

  // if any error occurs while fetching questions from the server
  // we should show message from the server or a default message
  useEffect(() => {
    const error = getQuestionsError?.graphQLErrors || verifyAuthQuestionsError?.graphQLErrors;
    console.log({ error });
    if (error?.length > 0) {
      setErrorTitle("Could not initiate your verification.");
      setErrorMessage(error[0].displayMessage || DEFAULT_ERROR_MESSAGE);
      if (error[0]?.code === "403") {
        lockUser();
      }
    } else {
      setErrorMessage("");
      setErrorTitle("");
    }
  },[getQuestionsError, verifyAuthQuestionsError]);

  // otp flow and questionnaires flow should be mutually exclusive
  // if we are in otp flow, we should not show the questions and vice versa
  // we need to check if we are in otp flow or questionnaires flow
  const isInOtpPreferenceStep = questions?.some((q) => q.key === "IDM_Choice");
  const isInOtpInputStep = questions?.some((q) => q.key === "IDM_PINVERIFY");
  const isInQuesstionare =
    !isInOtpInputStep && !isInOtpPreferenceStep && questionsAreValid;

  useEffect(() => {
    let eventName;
    switch (true) {
      case isInOtpPreferenceStep:
        eventName = "CMD_AUTH_OTP_PREFERENCE_START";
        break;
      case isInOtpInputStep:
        eventName = "CMD_AUTH_OTP_FLOW_START";
        break;
      case isInQuesstionare:
        eventName = "CMD_AUTH_KBA_FLOW_START";
        break;
      default:
        eventName = null;
    }

    if (eventName) {
      window.dataLayer?.push({
        event: eventName,
      });
    }
  }, [isInOtpPreferenceStep, isInOtpInputStep, isInQuesstionare]);

  const selectAnswer = (questionId, answerId, key, clearAll = false) => {
    const newSelectedAnswers = new Map(selectedAnswers);
    if (clearAll) {
      newSelectedAnswers.clear();
    }
    newSelectedAnswers.set(questionId, { answerId, key });
    setSelectedAnswers(newSelectedAnswers);
  };

  useEffect(() => {
    if (isInOtpInputStep && questions.length > 0) {
      // reset answers and timer when questions are updated
      selectAnswer(
        questions[0].questionId,
        questions[0].answerChoices[0].answerId,
        questions[0].key,
        true,
      );
      setOtpExireTime(DEFAULT_TIMER);
    }
  }, [questions]);

  let title = "";
  let subtitle = "";
  if (isInOtpPreferenceStep) {
    title = "Enter verification code";
    subtitle = "How would you like to receive your one-time passcode?";
  } else if (isInOtpInputStep) {
    title = "Enter verification code";
    subtitle =
      "Please enter your 5 digit code within 15 minutes (limit 3 attempts).";
  } else if (isInQuesstionare) {
    title = "Answer a few security questions";
    subtitle = "Help us verify your identity.";
  }

  const verifyAuthQuestions = async (resetOtp = false) => {
    setFormError("");
    if (isInOtpPreferenceStep && selectedAnswers.size === 0) {
      setFormError("Please select a delivery method");
      return;
    }
    if (isInOtpInputStep && otp.length !== 5 && !resetOtp) {
      setFormError("Please enter a 5 digit code");
      return;
    }

    const actionType = isInQuesstionare
      ? "KBA"
      : isInOtpInputStep
        ? resetOtp
          ? "NEW_PIN"
          : "OTP"
        : "OTP_CHOICE";
    window.dataLayer?.push({
      event: `CMD_VERIFY_${actionType}`,
    });

    const { data: verifyAuthResult } = await verifyAuthQuestionsMutation({
      variables: {
        verifyAnswerInput: {
          blackbox,
          fullfillmentKey,
          answers: Array.from(selectedAnswers).map(
            ([questionId, { answerId, key }]) => ({
              questionId,
              answerId,
              key,
              // eslint-disable-next-line no-nested-ternary
              userInput: isInOtpInputStep
                ? resetOtp
                  ? "NEWPIN"
                  : otp
                : undefined,
            }),
          ),
        },
      },
    });
    if (
      verifyAuthResult?.verifyTransUnionAuthentication?.status === "Incorrect"
    ) {
      const lockedUntil = moment().add(30, "days"); // TODO ideally this should come from the server
      setErrorTitle("Verification unsuccessful");
      setErrorMessage(
        `You’ve reached the verification attempt limit. Please try again after ${lockedUntil.format(
          "MM/DD/YYYY",
        )} or contact support.`,
      );
      lockUser();

      // status "Incorrect" should only ever fire for
      // actionType OTP, NEW_PIN, or KBA
      window.dataLayer?.push({
        event: `CMD_VERIFY_INCORRECT_${actionType}`,
      });
      return;
    }

    if (isInOtpPreferenceStep) {
      const newQuestions =
        verifyAuthResult.verifyTransUnionAuthentication?.multiChoiceQuestions;
      setQuestions(newQuestions);
      return;
    }

    if (isInOtpInputStep) {
      if (
        verifyAuthResult?.verifyTransUnionAuthentication?.status ===
        "InProgress"
      ) {
        if (resetOtp) {
          setErrorTitle("New code sent to your phone");
          setOtpExireTime(DEFAULT_TIMER);
        } else {
          // wrong otp entered
          setErrorTitle("Verification unsuccessful");
        }
        setErrorMessage("Please confirm your code and try again.");
        return;
      }
    }

    if (
      verifyAuthResult?.verifyTransUnionAuthentication?.status === "SUCCESS"
    ) {
      // status "SUCCESS" should only ever fire for
      // actionType OTP or KBA
      window.dataLayer?.push({
        event: `CMD_VERIFY_SUCCESS_${actionType}`,
      });
      onContinue();
    }
  };

  const requestNewOtp = () => {
    setOtp("");
    setErrorMessage("");
    selectAnswer(
      questions[0].questionId,
      questions[0].answerChoices[0].answerId,
      questions[0].key,
      true,
    );
    verifyAuthQuestions(true);
  };

  return (
    <div className="flex flex-col">
      {questionsLoading ? (
        <div className="loader" style={{ width: 40, height: 40 }} />
      ) : (
        <div>
          <p className="text-2xl font-bold" style={{ lineHeight: "120%" }}>
            {title}
          </p>
          <p>{subtitle}</p>
          {(isInQuesstionare || isInOtpPreferenceStep) && (
            <div>
              {questions.map((question) => (
                <SingleQuestion
                  key={question.questionText}
                  question={question}
                  onChoiceSelect={selectAnswer}
                />
              ))}
            </div>
          )}
          {isInOtpInputStep && (
            <div className="mt-6">
              <TextField
                label=""
                type="number"
                variant="outlined"
                placeholder="Enter 5 digit code"
                disabled={isLocked}
                onChange={(e) =>
                  e.target.value.length <= 5 && setOtp(e.target.value)
                }
                value={otp}
              />
              {!loading && !isLocked && (
                <div className="mt-6">
                  <Countdown
                    date={otpExireTime}
                    renderer={({ minutes, seconds }) => (
                      <p>
                        Current code expires in{" "}
                        <span className="font-bold">
                          {minutes.toString().padStart(2, "0")}:
                          {seconds.toString().padStart(2, "0")}
                        </span>
                      </p>
                    )}
                  />
                </div>
              )}
              <div className="mt-6 flex">
                <p>Didn’t receive a text or call?</p>
                <button
                  type="button"
                  className="underline ml-1"
                  onClick={requestNewOtp}
                  disabled={isLocked}
                >
                  Try again
                </button>
              </div>
            </div>
          )}
          <CreditMonitoringErrorBox
            className="mt-6"
            isVisible={!!errorMessage}
            title={errorTitle}
            message={errorMessage}
          />
        </div>
      )}
      {(questionsAreValid || isLocked) && (
        <div
          className={
            isMobile ? "sticky bottom-0 left-0 right-0 bg-white py-2" : ""
          }
        >
          <VerificationContinueButton
            loading={loading}
            title={isLocked ? "Return to dashboard" : "Continue"}
            onClick={() =>
              (isLocked ? window.location.reload() : verifyAuthQuestions())
            }
          />
          <p className="text-red-500 mt-2">{formError}</p>
        </div>
      )}
    </div>
  );
};
